/**
 * SidebarNav Component
 *
 * Navigation groups and items for the sidebar.
 * Includes main navigation, settings, and user menu sections.
 */

import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  Tractor,
  ReceiptText,
  Users,
  Factory,
  BarChart3,
  Settings,
  User,
  LogOut,
  ShoppingCart,
  FileText,
  Tag,
  Package,
  Gauge,
  Scissors,
  CalendarRange,
  Beaker,
  Wheat,
  Cuboid,
  ClipboardList,
  ClipboardCheck,
  Group,
  Warehouse,
  Building2,
  Receipt,
  MapPin,
  Calculator,
  TrendingUp,
  PieChart,
  Target,
  Zap,
  Dna,
  Workflow,
  Bell,
  Flame,
  Shuffle,
  Timer,
  CalendarClock,
  UserCheck,
  TreePine,
  Store,
} from 'lucide-react'
import { AlertBadgeCompact } from '@/components/alerts'
import RegistrationBadge from '@/components/registration/RegistrationBadge'
import SidebarGroup from './SidebarGroup'
import SidebarItem from './SidebarItem'

// Navigation structure
const mainNavigation = [
  {
    id: 'home',
    label: 'Home',
    icon: Tractor,
    path: '/'
  },
  {
    id: 'tasks',
    label: 'Tasks',
    icon: ClipboardList,
    path: '/tasks'
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: ReceiptText,
    children: [
      { id: 'sales-timeline', label: 'Timeline', path: '/sales/timeline', icon: TrendingUp },
      { id: 'orders', label: 'Orders', path: '/sales/orders', icon: ShoppingCart },
      { id: 'invoicing', label: 'Invoicing', path: '/sales/invoicing', icon: FileText },
{ id: 'pricing', label: 'Pricing', path: '/sales/pricing', icon: Tag },
      { id: 'customers', label: 'Customers', path: '/customers', icon: Users, badge: RegistrationBadge },
      { id: 'markets', label: 'Markets', path: '/sales/markets', icon: Store }
    ]
  },
  {
    id: 'production',
    label: 'Production',
    icon: Factory,
    children: [
      { id: 'timeline', label: 'Timeline', path: '/production', icon: CalendarRange },
      { id: 'strains', label: 'Strains', path: '/production/strains', icon: Dna },
      { id: 'process', label: 'Processes', path: '/production/process', icon: Workflow },
      { id: 'liquid-cultures', label: 'Liquid Cultures', path: '/production/lc', icon: Beaker },
      { id: 'spawn', label: 'Spawn', path: '/production/spawn', icon: Wheat },
      { id: 'substrate', label: 'Substrate', path: '/production/substrate', icon: Cuboid },
      { id: 'heat-cycling', label: 'Heat Cycling', path: '/production/heat-cycling', icon: Flame },
      { id: 'inoculations', label: 'Inoculations', path: '/production/inoculations', icon: Group },
      { id: 'shoogling', label: 'Shoogling', path: '/production/shoogling', icon: Shuffle },
      { id: 'incubation', label: 'Incubation', path: '/production/incubation', icon: Timer },
      { id: 'harvests', label: 'Harvests', path: '/production/harvests', icon: Scissors },
      { id: 'substrate-logs', label: 'Substrate Logs', path: '/production/substrate-logs', icon: TreePine },
      { id: 'inspections', label: 'Inspections', path: '/production/inspections', icon: ClipboardCheck },
      { id: 'timetable', label: 'Timetable', path: '/production/timetable', icon: CalendarClock }
    ]
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: Warehouse,
    children: [
      { id: 'overview', label: 'Overview', path: '/inventory', icon: Gauge },
      { id: 'items', label: 'Items', path: '/inventory/items', icon: Package },
      { id: 'purchases', label: 'Purchases', path: '/inventory/purchases', icon: Receipt },
      { id: 'suppliers', label: 'Suppliers', path: '/inventory/suppliers', icon: Building2 },
    ]
  },
  {
    id: 'costs',
    label: 'Costs',
    icon: Calculator,
    children: [
      { id: 'summary', label: 'Summary', path: '/costs/summary', icon: BarChart3 },
      { id: 'fixed', label: 'Fixed Costs', path: '/costs/fixed', icon: Receipt },
      { id: 'energy', label: 'Energy', path: '/costs/energy', icon: Zap }
    ]
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: TrendingUp,
    children: [
      { id: 'profitability', label: 'Profitability', path: '/analytics/profitability', icon: PieChart },
      { id: 'breakeven', label: 'Breakeven', path: '/analytics/breakeven', icon: Target }
    ]
  },
  {
    id: 'reporting',
    label: 'Reporting',
    icon: BarChart3,
    path: '/reporting'
  },
  {
    id: 'alerts',
    label: 'Alerts',
    icon: Bell,
    path: '/alerts',
    badge: AlertBadgeCompact
  }
]

const settingsNavigation = [
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    children: [
      { id: 'products', label: 'Products', path: '/settings/products', icon: Package },
      { id: 'locations', label: 'Locations', path: '/settings/locations', icon: MapPin },
      { id: 'system', label: 'System', path: '/settings/system', icon: Gauge },
      { id: 'registration-requests', label: 'Portal Requests', path: '/settings/registration-requests', icon: UserCheck }
    ]
  }
]

export default function SidebarNav({ isExpanded, currentPath, user, onLogout }) {
  const navigate = useNavigate()

  const handleNavigate = (path) => {
    navigate(path)
  }

  // Check if a path is active (exact match or child route)
  const isPathActive = (path) => {
    if (path === '/') {
      return currentPath === '/'
    }
    return currentPath.startsWith(path)
  }

  // Check if a group has an active child
  const hasActiveChild = (children) => {
    return children?.some(child => isPathActive(child.path))
  }

  return (
    <nav className="flex flex-col flex-1 py-4 overflow-y-auto overflow-x-hidden">
      {/* Main Navigation */}
      <div className="flex-1 space-y-1 px-2">
        {mainNavigation.map((item) => {
          if (item.children) {
            return (
              <SidebarGroup
                key={item.id}
                label={item.label}
                icon={item.icon}
                isExpanded={isExpanded}
                isActive={hasActiveChild(item.children)}
              >
                {item.children.map((child) => (
                  <SidebarItem
                    key={child.id}
                    label={child.label}
                    icon={child.icon}
                    isExpanded={isExpanded}
                    isActive={isPathActive(child.path)}
                    onClick={() => handleNavigate(child.path)}
                    badge={child.badge}
                    isNested
                  />
                ))}
              </SidebarGroup>
            )
          }

          return (
            <SidebarItem
              key={item.id}
              label={item.label}
              icon={item.icon}
              isExpanded={isExpanded}
              isActive={isPathActive(item.path)}
              onClick={() => handleNavigate(item.path)}
              badge={item.badge}
            />
          )
        })}
      </div>

      {/* Spacer */}
      <div className="flex-shrink-0 h-4" />

      {/* Settings Section */}
      <div className="border-t border-border pt-4 space-y-1 px-2">
        {settingsNavigation.map((item) => {
          if (item.children) {
            return (
              <SidebarGroup
                key={item.id}
                label={item.label}
                icon={item.icon}
                isExpanded={isExpanded}
                isActive={hasActiveChild(item.children)}
              >
                {item.children.map((child) => (
                  <SidebarItem
                    key={child.id}
                    label={child.label}
                    icon={child.icon}
                    isExpanded={isExpanded}
                    isActive={isPathActive(child.path)}
                    onClick={() => handleNavigate(child.path)}
                    badge={child.badge}
                    isNested
                  />
                ))}
              </SidebarGroup>
            )
          }

          return (
            <SidebarItem
              key={item.id}
              label={item.label}
              icon={item.icon}
              isExpanded={isExpanded}
              isActive={isPathActive(item.path)}
              onClick={() => handleNavigate(item.path)}
            />
          )
        })}

        {/* User Menu */}
        <div className="pt-2 border-t border-border mt-2">
          <SidebarItem
            label={user?.email?.split('@')[0] || 'User'}
            icon={User}
            isExpanded={isExpanded}
            isActive={false}
            onClick={() => {}}
            disabled
          />
          <SidebarItem
            label="Sign out"
            icon={LogOut}
            isExpanded={isExpanded}
            isActive={false}
            onClick={onLogout}
            variant="destructive"
          />
        </div>
      </div>
    </nav>
  )
}
